import {useCallback, useEffect, useRef, useState} from 'react';
import {LiveSessionSummary, liveSessionsApi} from '../../../../services/liveSessionsApi';
import {socialFeedApi} from '../../../../services/socialFeedApi';
import {isRequestCanceled} from '../utils';

export type LiveBroadcastPhase =
  | 'idle'
  | 'requesting-media'
  | 'starting-session'
  | 'connecting'
  | 'live'
  | 'ending';

interface UseHomeLiveBroadcastArgs {
  token: string | null;
  userName?: string;
  onSessionStarted: (session: LiveSessionSummary) => void;
  onSessionEnded: (sessionId: string) => void;
  refreshLiveSessions: () => Promise<void>;
}

const stopMediaStream = (stream: MediaStream) => {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
};

const ensureBroadcastMediaAccess = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser does not support camera and microphone broadcasting.');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: true,
  });

  stopMediaStream(stream);
};

const getLiveSessionTitle = (userName?: string) => {
  const normalizedUserName = userName?.trim();
  return `${normalizedUserName || 'Creator'} is live`;
};

export const useHomeLiveBroadcast = ({
  token,
  userName,
  onSessionStarted,
  onSessionEnded,
  refreshLiveSessions,
}: UseHomeLiveBroadcastArgs) => {
  const startRequestControllerRef = useRef<AbortController | null>(null);
  const endRequestControllerRef = useRef<AbortController | null>(null);
  const previewUploadControllerRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<LiveSessionSummary | null>(null);
  const ignoreDisconnectRef = useRef(false);
  const startAttemptIdRef = useRef(0);
  const previewCapturedSessionIdsRef = useRef<Set<string>>(new Set());
  const livePreviewObjectUrlsRef = useRef<Set<string>>(new Set());

  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [broadcastSession, setBroadcastSession] = useState<LiveSessionSummary | null>(null);
  const [broadcastAccessToken, setBroadcastAccessToken] = useState<string | null>(null);
  const [broadcastLiveKitUrl, setBroadcastLiveKitUrl] = useState<string | null>(null);
  const [broadcastPhase, setBroadcastPhase] = useState<LiveBroadcastPhase>('idle');
  const [broadcastError, setBroadcastError] = useState<string | null>(null);

  const persistSession = useCallback((session: LiveSessionSummary | null) => {
    sessionRef.current = session;
    setBroadcastSession(session);
  }, []);

  const clearBroadcastConnection = useCallback(() => {
    setBroadcastAccessToken(null);
    setBroadcastLiveKitUrl(null);
  }, []);

  const revokeLivePreviewObjectUrl = useCallback((previewImageUrl?: string | null) => {
    if (!previewImageUrl?.startsWith('blob:')) {
      return;
    }

    URL.revokeObjectURL(previewImageUrl);
    livePreviewObjectUrlsRef.current.delete(previewImageUrl);
  }, []);

  const revokeLivePreviewObjectUrls = useCallback(() => {
    livePreviewObjectUrlsRef.current.forEach((previewImageUrl) => {
      URL.revokeObjectURL(previewImageUrl);
    });
    livePreviewObjectUrlsRef.current.clear();
  }, []);

  const cleanupFailedSession = useCallback(async (sessionId: string) => {
    if (!token) {
      return;
    }

    endRequestControllerRef.current?.abort();
    const controller = new AbortController();
    endRequestControllerRef.current = controller;

    try {
      await liveSessionsApi.endSession(token, sessionId, controller.signal);
    } catch (error) {
      if (!isRequestCanceled(error)) {
        console.error('Failed to clean up interrupted live session:', error);
      }
    } finally {
      if (endRequestControllerRef.current === controller) {
        endRequestControllerRef.current = null;
      }
    }
  }, [token]);

  const resetBroadcastState = useCallback(() => {
    startAttemptIdRef.current += 1;
    startRequestControllerRef.current?.abort();
    endRequestControllerRef.current?.abort();
    previewUploadControllerRef.current?.abort();
    previewUploadControllerRef.current = null;
    revokeLivePreviewObjectUrls();
    persistSession(null);
    clearBroadcastConnection();
    setBroadcastPhase('idle');
    setBroadcastError(null);
    setIsBroadcastModalOpen(false);
  }, [clearBroadcastConnection, persistSession, revokeLivePreviewObjectUrls]);

  const openBroadcastModal = useCallback(async () => {
    if (!token) {
      setBroadcastError('You must be signed in to go live.');
      return;
    }

    const attemptId = startAttemptIdRef.current + 1;
    startAttemptIdRef.current = attemptId;
    startRequestControllerRef.current?.abort();
    ignoreDisconnectRef.current = false;
    clearBroadcastConnection();
    persistSession(null);
    setBroadcastError(null);
    setBroadcastPhase('requesting-media');
    setIsBroadcastModalOpen(true);

    try {
      await ensureBroadcastMediaAccess();
    } catch (error) {
      if (attemptId !== startAttemptIdRef.current) {
        return;
      }

      setBroadcastPhase('idle');
      setBroadcastError(error instanceof Error ? error.message : 'Camera and microphone access is required to go live.');
      return;
    }

    if (attemptId !== startAttemptIdRef.current) {
      return;
    }

    const controller = new AbortController();
    startRequestControllerRef.current = controller;

    try {
      setBroadcastPhase('starting-session');
      const response = await liveSessionsApi.startSession(
        token,
        {title: getLiveSessionTitle(userName)},
        controller.signal
      );

      if (controller.signal.aborted || attemptId !== startAttemptIdRef.current) {
        return;
      }

      persistSession(response.session);
      setBroadcastAccessToken(response.publisherToken);
      setBroadcastLiveKitUrl(response.liveKitUrl);
      setBroadcastPhase('connecting');
      onSessionStarted(response.session);
      await refreshLiveSessions();
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setBroadcastPhase('idle');
      setBroadcastError(error instanceof Error ? error.message : 'Failed to start the live session');
    } finally {
      if (startRequestControllerRef.current === controller) {
        startRequestControllerRef.current = null;
      }
    }
  }, [clearBroadcastConnection, onSessionStarted, persistSession, refreshLiveSessions, token, userName]);

  const closeBroadcastModal = useCallback(() => {
    const currentSession = sessionRef.current;

    if (broadcastPhase === 'live' || broadcastPhase === 'ending') {
      return;
    }

    if (currentSession?.id && broadcastAccessToken) {
      ignoreDisconnectRef.current = true;
      void cleanupFailedSession(currentSession.id);
      onSessionEnded(currentSession.id);
    }

    resetBroadcastState();
  }, [broadcastAccessToken, broadcastPhase, cleanupFailedSession, onSessionEnded, resetBroadcastState]);

  const handleBroadcastConnected = useCallback(() => {
    setBroadcastPhase('live');
    setBroadcastError(null);

    if (!sessionRef.current) {
      return;
    }

    const nextSession: LiveSessionSummary = {
      ...sessionRef.current,
      status: 'live',
      startedAt: sessionRef.current.startedAt || new Date().toISOString(),
    };

    persistSession(nextSession);
    onSessionStarted(nextSession);
    void refreshLiveSessions();
  }, [onSessionStarted, persistSession, refreshLiveSessions]);

  const handleBroadcastFailure = useCallback((message: string) => {
    const currentSessionId = sessionRef.current?.id;

    setBroadcastPhase('idle');
    ignoreDisconnectRef.current = true;
    setBroadcastError(message);
    clearBroadcastConnection();

    if (currentSessionId) {
      void cleanupFailedSession(currentSessionId);
      onSessionEnded(currentSessionId);
      void refreshLiveSessions();
    }
  }, [cleanupFailedSession, clearBroadcastConnection, onSessionEnded, refreshLiveSessions]);

  const handleBroadcastPreviewCapture = useCallback(async (previewBlob: Blob) => {
    const currentSession = sessionRef.current;

    if (
      !token ||
      !currentSession?.id ||
      currentSession.previewImageUrl ||
      previewCapturedSessionIdsRef.current.has(currentSession.id)
    ) {
      return;
    }

    previewCapturedSessionIdsRef.current.add(currentSession.id);
    previewUploadControllerRef.current?.abort();

    const controller = new AbortController();
    previewUploadControllerRef.current = controller;
    let uploadSessionId: string | null = null;
    let localPreviewImageUrl: string | null = null;
    let previewAttached = false;

    try {
      localPreviewImageUrl = URL.createObjectURL(previewBlob);
      livePreviewObjectUrlsRef.current.add(localPreviewImageUrl);

      const localPreviewSession: LiveSessionSummary = {
        ...currentSession,
        previewImageUrl: localPreviewImageUrl,
        previewCapturedAt: new Date().toISOString(),
      };

      persistSession(localPreviewSession);
      onSessionStarted(localPreviewSession);

      const previewFile = new File(
        [previewBlob],
        `live-preview-${currentSession.id}.jpg`,
        {
          type: previewBlob.type || 'image/jpeg',
          lastModified: Date.now(),
        }
      );
      const uploadSession = await socialFeedApi.createMediaUploadSession(token, {
        purpose: 'live-preview',
        contentType: previewFile.type,
        fileName: previewFile.name,
        fileSize: previewFile.size,
      }, controller.signal);

      uploadSessionId = uploadSession.uploadSession.id;

      await socialFeedApi.uploadSinglePutSessionFile(uploadSession, previewFile, {
        signal: controller.signal,
      });

      const response = await liveSessionsApi.updatePreviewImage(
        token,
        currentSession.id,
        {uploadSessionId},
        controller.signal
      );

      if (controller.signal.aborted) {
        return;
      }

      previewAttached = true;
      persistSession(response.session);
      onSessionStarted(response.session);
      revokeLivePreviewObjectUrl(localPreviewImageUrl);
      void refreshLiveSessions().catch((refreshError) => {
        if (!isRequestCanceled(refreshError)) {
          console.error('Failed to refresh live sessions after preview update:', refreshError);
        }
      });
    } catch (error) {
      previewCapturedSessionIdsRef.current.delete(currentSession.id);

      if (isRequestCanceled(error)) {
        if (uploadSessionId && !previewAttached) {
          await socialFeedApi.abortMediaUploadSession(token, uploadSessionId).catch(() => undefined);
        }

        return;
      }

      if (uploadSessionId && !previewAttached) {
        await socialFeedApi.abortMediaUploadSession(token, uploadSessionId).catch(() => undefined);
      }

      console.error('Failed to upload live preview image:', error);
    } finally {
      if (previewUploadControllerRef.current === controller) {
        previewUploadControllerRef.current = null;
      }
    }
  }, [onSessionStarted, persistSession, refreshLiveSessions, revokeLivePreviewObjectUrl, token]);

  const handleBroadcastDisconnected = useCallback(() => {
    if (ignoreDisconnectRef.current) {
      ignoreDisconnectRef.current = false;
      return;
    }

    const currentSessionId = sessionRef.current?.id;

    setBroadcastPhase('idle');
    clearBroadcastConnection();
    setBroadcastError('Your live session disconnected.');

    if (currentSessionId) {
      onSessionEnded(currentSessionId);
      void refreshLiveSessions();
    }
  }, [clearBroadcastConnection, onSessionEnded, refreshLiveSessions]);

  const endBroadcast = useCallback(async () => {
    const currentSession = sessionRef.current;

    if (!token || !currentSession?.id || broadcastPhase !== 'live') {
      return;
    }

    endRequestControllerRef.current?.abort();
    previewUploadControllerRef.current?.abort();
    const controller = new AbortController();
    endRequestControllerRef.current = controller;
    ignoreDisconnectRef.current = true;
    setBroadcastPhase('ending');
    setBroadcastError(null);

    try {
      const response = await liveSessionsApi.endSession(token, currentSession.id, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      onSessionEnded(currentSession.id);
      persistSession(response.session);
      clearBroadcastConnection();
      setBroadcastPhase('idle');
      setIsBroadcastModalOpen(false);
      await refreshLiveSessions();
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      ignoreDisconnectRef.current = false;
      setBroadcastPhase('live');
      setBroadcastError(error instanceof Error ? error.message : 'Failed to end the live session');
    } finally {
      if (endRequestControllerRef.current === controller) {
        endRequestControllerRef.current = null;
      }
    }
  }, [broadcastPhase, clearBroadcastConnection, onSessionEnded, persistSession, refreshLiveSessions, token]);

  useEffect(() => {
    return () => {
      startRequestControllerRef.current?.abort();
      endRequestControllerRef.current?.abort();
      previewUploadControllerRef.current?.abort();
      revokeLivePreviewObjectUrls();
    };
  }, [revokeLivePreviewObjectUrls]);

  return {
    isBroadcastModalOpen,
    broadcastSession,
    broadcastAccessToken,
    broadcastLiveKitUrl,
    broadcastPhase,
    broadcastError,
    openBroadcastModal,
    closeBroadcastModal,
    handleBroadcastConnected,
    handleBroadcastDisconnected,
    handleBroadcastError: handleBroadcastFailure,
    handleBroadcastMediaDeviceFailure: handleBroadcastFailure,
    handleBroadcastPreviewCapture,
    endBroadcast,
  };
};
